import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LandingPage from "@/pages/LandingPage";
import SelleTransLandingPage from "@/pages/SelleTransLandingPage";
import PartnersPage from "@/pages/PartnersPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import OnboardingPage from "@/pages/OnboardingPage";
import OnboardingWizardPage from "@/pages/OnboardingWizardPage";
import DashboardLayout from "@/layouts/DashboardLayout";
import DashboardOverview from "@/pages/DashboardOverview";
import SecretsPage from "@/pages/SecretsPage";
import VerificationPage from "@/pages/VerificationPage";
import ShipmentsPage from "@/pages/ShipmentsPage";
import TransactionsPage from "@/pages/TransactionsPage";
import WalletPage from "@/pages/WalletPage";
import CoverageTermsPage from "@/pages/CoverageTermsPage";
import InsuranceDeclarationPage from "@/pages/InsuranceDeclarationPage";
import SameDayDeliveryPage from "@/pages/SameDayDeliveryPage";
import SendlePage from "@/pages/SendlePage";
import OfficeNetworkPage from "@/pages/OfficeNetworkPage";
import ProtectPage from "@/pages/ProtectPage";
import ClientAdminPage from "@/pages/ClientAdminPage";
import PlatformAdminPage from "@/pages/PlatformAdminPage";
import ShipStationAccountsPage from "@/pages/ShipStationAccountsPage";
import AdminKysQueuePage from "@/pages/AdminKysQueuePage";
import SelleTransDashboardPage from "@/pages/SelleTransDashboardPage";
import WebShipPage from "@/pages/WebShipPage";
import Selle10Page from "@/pages/Selle10Page";
import SellingHubPage from "@/pages/SellingHubPage";
import ProgramsPage from "@/pages/ProgramsPage";
import AdminProgramsPage from "@/pages/AdminProgramsPage";
import AdminPasswordsPage from "@/pages/AdminPasswordsPage";
import AdminSelleGardConfigPage from "@/pages/AdminSelleGardConfigPage";
import AdminSelleGardCreditPage from "@/pages/AdminSelleGardCreditPage";
import AdminRevenuePage from "@/pages/AdminRevenuePage";
import AdminMetricsPage from "@/pages/AdminMetricsPage";
import AdminAmlPage from "@/pages/AdminAmlPage";
import AdminCoverageTermsPage from "@/pages/AdminCoverageTermsPage";
import AdminHealthPage from "@/pages/AdminHealthPage";
import AdminReconciliationPage from "@/pages/AdminReconciliationPage";
import TrackingPage from "@/pages/TrackingPage";
import BrokerReportPage from "@/pages/BrokerReportPage";
import UnifiedBillingPage from "@/pages/UnifiedBillingPage";
import LossRatioReportPage from "@/pages/LossRatioReportPage";
import AdminMerchantsPage from "@/pages/AdminMerchantsPage";
import AdminAddressBookPage from "@/pages/AdminAddressBookPage";
import AdminSetupWizardPage from "@/pages/AdminSetupWizardPage";
import InsuranceReportingPage from "@/pages/InsuranceReportingPage";
import CarrierPnLPage from "@/pages/CarrierPnLPage";
import ClaimsPage from "@/pages/ClaimsPage";
import SelleShopPage from "@/pages/SelleShopPage";
import RiskChoiceAuditPage from "@/pages/RiskChoiceAuditPage";
import DigestPage from "@/pages/DigestPage";
import ShippingActivityPage from "@/pages/ShippingActivityPage";
import BuyerVerifyPage from "@/pages/BuyerVerifyPage";
import SelleCheckPage from "@/pages/SelleCheckPage";
import PayPage from "@/pages/PayPage";
import SecuritySettingsPage from "@/pages/SecuritySettingsPage";
import NotificationSettingsPage from "@/pages/NotificationSettingsPage";
import EliteShipmentsPage from "@/pages/EliteShipmentsPage";
import ShippingGatewayPage from "@/pages/ShippingGatewayPage";
import PremiumToolsPage from "@/pages/PremiumToolsPage";
import ClickPicPage from "@/pages/ClickPicPage";
import LegalIndexPage from "@/pages/LegalIndexPage";
import MemberLegalCenter from "@/pages/MemberLegalCenter";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/verify/:fingerprint" element={<BuyerVerifyPage />} />
          <Route path="/pay/:token" element={<PayPage />} />
          <Route path="/selletrans" element={<SelleTransLandingPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          {/* Legal (public) — routes for the footer links + shareable URLs. */}
          <Route path="/legal"        element={<LegalIndexPage />} />
          <Route path="/legal/:slug"  element={<LegalIndexPage />} />
          {/* Convenience aliases so classic /privacy, /terms links still work. */}
          <Route path="/privacy"          element={<LegalIndexPage />} />
          <Route path="/terms"            element={<LegalIndexPage />} />
          <Route path="/shipping-terms"   element={<LegalIndexPage />} />
          <Route path="/regulatory"       element={<LegalIndexPage />} />
          <Route path="/aml-compliance"   element={<LegalIndexPage />} />
          <Route path="/non-custodial"    element={<LegalIndexPage />} />
          <Route path="/restricted-prohibited-products" element={<LegalIndexPage />} />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          />
          {/* Sin PublicRoute: el enlace del correo tiene que abrir aunque haya una sesion abierta. */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/verify"
            element={
              <ProtectedRoute>
                <OnboardingWizardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kys"
            element={
              <ProtectedRoute>
                <OnboardingWizardPage />
              </ProtectedRoute>
            }
          />
          
          {/* Protected Dashboard Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardOverview />} />
            <Route path="secrets" element={<SecretsPage />} />
            <Route path="shipments" element={<ShipmentsPage />} />
            {/* "New Shipment" was a draft that never bought a label; shipping is bought in Rate Shop (12-oct). */}
            <Route path="shipments/new" element={<Navigate to="/dashboard/rate-shop" replace />} />
            <Route path="verification" element={<VerificationPage />} />
            <Route path="transactions" element={<TransactionsPage />} />
            <Route path="wallet" element={<WalletPage />} />
            <Route path="coverage-terms" element={<CoverageTermsPage />} />
            <Route path="insurance" element={<InsuranceDeclarationPage />} />
            <Route path="cargo" element={<Navigate to="/dashboard/rate-shop" replace />} />
            <Route path="sameday" element={<SameDayDeliveryPage />} />
            <Route path="sendle" element={<SendlePage />} />
            <Route path="office-network" element={<OfficeNetworkPage />} />
            <Route path="protect" element={<ProtectPage />} />
            <Route path="protect/success" element={<ProtectPage />} />
            <Route path="admin" element={<ClientAdminPage />} />
            <Route path="platform-admin" element={<PlatformAdminPage />} />
            <Route path="admin/shipstation-accounts" element={<ShipStationAccountsPage />} />
            <Route path="admin/kys" element={<AdminKysQueuePage />} />
            <Route path="selletrans" element={<SelleTransDashboardPage />} />
            <Route path="webship" element={<WebShipPage />} />
            <Route path="sellecheck" element={<SelleCheckPage />} />
            <Route path="get-paid" element={<Navigate to="/dashboard/sellecheck" replace />} />
            <Route path="sellegard" element={<WebShipPage />} />
            <Route path="selle10" element={<Selle10Page />} />
            <Route path="sell" element={<SellingHubPage />} />
            <Route path="programs" element={<ProgramsPage />} />
            <Route path="admin/programs" element={<AdminProgramsPage />} />
            <Route path="admin/passwords" element={<AdminPasswordsPage />} />
            <Route path="admin/sellegard-config" element={<AdminSelleGardConfigPage />} />
            <Route path="admin/sellegard-credit" element={<AdminSelleGardCreditPage />} />
            <Route path="admin/revenue" element={<AdminRevenuePage />} />
            <Route path="admin/metrics" element={<AdminMetricsPage />} />
            <Route path="admin/aml-documents" element={<AdminAmlPage />} />
            <Route path="admin/coverage-terms" element={<AdminCoverageTermsPage />} />
            <Route path="admin/health" element={<AdminHealthPage />} />
            <Route path="admin/reconciliation" element={<AdminReconciliationPage />} />
            <Route path="tracking" element={<TrackingPage />} />
            <Route path="broker/report" element={<BrokerReportPage />} />
            <Route path="billing" element={<UnifiedBillingPage />} />
            <Route path="admin/reports/loss-ratio" element={<LossRatioReportPage />} />
            <Route path="admin/merchants" element={<AdminMerchantsPage />} />
            <Route path="admin/addresses" element={<AdminAddressBookPage />} />
            <Route path="admin/setup-wizard" element={<AdminSetupWizardPage />} />
            <Route path="insurance-reporting" element={<InsuranceReportingPage />} />
            <Route path="admin/reports/carrier-pnl" element={<CarrierPnLPage />} />
            <Route path="claims" element={<ClaimsPage />} />
            <Route path="selleshop" element={<SelleShopPage />} />
            <Route path="admin/riskchoice-audit" element={<RiskChoiceAuditPage />} />
            <Route path="digest" element={<DigestPage />} />
            <Route path="activity" element={<ShippingActivityPage />} />
            <Route path="settings/security" element={<SecuritySettingsPage />} />
            <Route path="settings/notifications" element={<NotificationSettingsPage />} />
            <Route path="elite" element={<EliteShipmentsPage />} />
            <Route path="pricesmart" element={<Navigate to="/dashboard/rate-shop" replace />} />
            <Route path="rate-shop" element={<ShippingGatewayPage />} />
            <Route path="premium-tools" element={<PremiumToolsPage />} />
            <Route path="click-pic" element={<ClickPicPage />} />
            <Route path="legal"          element={<MemberLegalCenter />} />
            <Route path="legal/:slug"    element={<MemberLegalCenter />} />
          </Route>
          
          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AuthProvider>
  );
}

export default App;
